import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";

import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
  TextField,
  InputAdornment,
  CircularProgress,
  Chip,
  useTheme,
  Button,
  IconButton,
  MenuItem,
  Pagination,
  Grid,
  DialogContent,
  Dialog,
  DialogTitle,
} from "@mui/material";

import {
  Search as SearchIcon,
  AccessTime as AccessTimeIcon,
  Logout as LogoutIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  LocationOn as LocationOnIcon,
  EventAvailable as EventAvailableIcon,
  Pending as PendingIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

import attendanceSvg from "assets/folders/attendance.svg";

import { fetchAttendanceRecords } from "store/features/company/attendanceManagement/attendanceRecords/attendanceRecordsActions";

import {
  selectAttendanceRecords,
  selectAttendanceRecordsLoading,
  selectAttendanceRecordsPagination,
} from "store/features/company/attendanceManagement/attendanceRecords/attendanceRecordsSelectors";

const AttendanceRecords = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const attendanceRecords = useSelector(selectAttendanceRecords);
  const loading = useSelector(selectAttendanceRecordsLoading);
  const pagination = useSelector(selectAttendanceRecordsPagination);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [checkInModalOpen, setCheckInModalOpen] = useState(false);
  const [checkOutModalOpen, setCheckOutModalOpen] = useState(false);
  const [selectedPicture, setSelectedPicture] = useState("");

  useEffect(() => {
    const params = {
      page: page,
      limit: limit,
    };
    if (statusFilter !== "all") params.status = statusFilter;
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;

    dispatch(fetchAttendanceRecords(params));
  }, [statusFilter, fromDate, toDate, page, limit]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, fromDate, toDate]);

  useEffect(() => {
    const timer = setTimeout(() => {}, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value));
    setPage(1);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setFromDate("");
    setToDate("");
    setPage(1);
  };

  const filteredRecords = useMemo(() => {
    let filtered = attendanceRecords;

    // Frontend search filter (only for searchTerm)
    if (searchTerm) {
      filtered = filtered.filter((record) => {
        const fullName =
          `${record.first_name} ${record.last_name}`.toLowerCase();
        const phone = record.phone || "";
        const status = record.status || "";
        const search = searchTerm.toLowerCase();

        return (
          fullName.includes(search) ||
          phone.includes(search) ||
          status.includes(search)
        );
      });
    }

    return filtered;
  }, [attendanceRecords, searchTerm]);

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatTimeOnly = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatMinutes = (minutes) => {
    if (!minutes || minutes === 0) return "0 min";
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hrs === 0) return `${mins} min`;
    return `${hrs}h ${mins}m`;
  };

  const getStatusColor = (status) => {
    const colors = {
      present: {
        bg: "#d1fae5",
        text: "#065f46",
        darkBg: "rgba(16,185,129,0.25)",
        darkText: "#6ee7b7",
      },
      absent: {
        bg: "#fee2e2",
        text: "#991b1b",
        darkBg: "rgba(239,68,68,0.25)",
        darkText: "#fca5a5",
      },
      late: {
        bg: "#fef3c7",
        text: "#92400e",
        darkBg: "rgba(245,158,11,0.25)",
        darkText: "#fde68a",
      },
    };
    return colors[status] || colors.present;
  };

  const StatusBadge = ({ status }) => {
    const colors = getStatusColor(status);
    const getIcon = () => {
      switch (status) {
        case "present":
          return <CheckCircleIcon sx={{ fontSize: 14 }} />;
        case "absent":
          return <CancelIcon sx={{ fontSize: 14 }} />;
        case "late":
          return <PendingIcon sx={{ fontSize: 14 }} />;
        default:
          return <CheckCircleIcon sx={{ fontSize: 14 }} />;
      }
    };
    return (
      <Chip
        icon={getIcon()}
        label={status?.charAt(0).toUpperCase() + status?.slice(1) || "Unknown"}
        size="small"
        sx={{
          textTransform: "capitalize",
          fontWeight: 600,
          backgroundColor: colors.bg,
          color: colors.text,
          ...(theme.palette.mode === "dark" && {
            backgroundColor: colors.darkBg,
            color: colors.darkText,
          }),
        }}
      />
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
            : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
        p: 3,
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              component="img"
              src={attendanceSvg}
              alt="Attendance"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
            />
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.mode === "dark" ? "#ffffff" : "#111827",
                }}
              >
                Attendance Records
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                }}
              >
                View and monitor employee attendance activities
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Box>

      {/* Search and Status Filters - Row 1 */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <TextField
          placeholder="Search by employee name or phone ...."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          // Remove onKeyPress={handleKeyPress}
          size="small"
          sx={{
            flex: 1,
            maxWidth: { sm: 350 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              backgroundColor:
                theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": { borderColor: "#f59e0b" },
              "&.Mui-focused fieldset": { borderColor: "#f59e0b" },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9ca3af" }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm("")}>
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Stack direction="row" spacing={1}>
          {["all", "present", "late", "absent"].map((filter) => (
            <Button
              key={filter}
              variant={statusFilter === filter ? "contained" : "outlined"}
              onClick={() => {
                setStatusFilter(filter);
                setPage(1);
              }}
              size="small"
              sx={{
                textTransform: "capitalize",
                borderRadius: "20px",
                px: 2,
                minWidth: 70,
                ...(statusFilter === filter && {
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  },
                }),
              }}
            >
              {filter === "all" ? "All" : filter}
            </Button>
          ))}
        </Stack>
      </Stack>

      {/* Filters  - From Date, To Date, Records per page, Clear */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: "12px",
          backgroundColor:
            theme.palette.mode === "dark" ? "#0f172a" : "#f9fafb",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              select
              fullWidth
              label="Records per page"
              size="small"
              value={limit}
              onChange={handleLimitChange}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
                },
                "& .MuiSelect-select": {
                  textAlign: "right",
                },
              }}
            >
              <MenuItem value={10} sx={{ justifyContent: "flex-end" }}>
                10 per page
              </MenuItem>
              <MenuItem value={25} sx={{ justifyContent: "flex-end" }}>
                25 per page
              </MenuItem>
              <MenuItem value={50} sx={{ justifyContent: "flex-end" }}>
                50 per page
              </MenuItem>
              <MenuItem value={100} sx={{ justifyContent: "flex-end" }}>
                100 per page
              </MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="From Date"
              type="date"
              size="small"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
                },
                "& .MuiInputLabel-root": {
                  transform: "translate(14px, -9px) scale(0.75)",
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
                  padding: "0 4px",
                  borderRadius: "4px",
                },
                "& input[type='date']::-webkit-calendar-picker-indicator": {
                  filter:
                    theme.palette.mode === "dark" ? "invert(1)" : "invert(0)",
                  cursor: "pointer",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="To Date"
              type="date"
              size="small"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
                },
                "& .MuiInputLabel-root": {
                  transform: "translate(14px, -9px) scale(0.75)",
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
                  padding: "0 4px",
                  borderRadius: "4px",
                },
                "& input[type='date']::-webkit-calendar-picker-indicator": {
                  filter:
                    theme.palette.mode === "dark" ? "invert(1)" : "invert(0)",
                  cursor: "pointer",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              sx={{
                borderRadius: "10px",
                height: 40,
                borderColor: "#f59e0b",
                color: "#f59e0b",
                "&:hover": {
                  borderColor: "#d97706",
                  backgroundColor: "rgba(245,158,11,0.1)",
                },
              }}
            >
              Clear Dates
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Loading */}
      {loading ? (
        <Box
          sx={{
            height: 400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress sx={{ color: "#f59e0b" }} />
            <Typography
              sx={{
                color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
              }}
            >
              Loading attendance records...
            </Typography>
          </Stack>
        </Box>
      ) : (
        <>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "16px",
              overflow: "hidden",
              border: `1px solid ${
                theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
              }`,
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 10px 25px -5px rgba(0,0,0,0.3)"
                  : "0 10px 25px -5px rgba(0,0,0,0.05)",
            }}
          >
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 1000 }}>
                <TableHead
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                  }}
                >
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                      ID
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                      Employee Details
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                      Check In
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                      Check Out
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                      Status Info
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                      Working Details
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                      Overtime
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                      Approval
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                        <EventAvailableIcon
                          sx={{ fontSize: 50, color: "#d1d5db", mb: 1 }}
                        />
                        <Typography>No attendance records found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRecords.map((record, index) => (
                      <TableRow
                        key={record.id}
                        component={motion.tr}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        {/* Column 1: ID */}
                        <TableCell>
                          <Typography
                            sx={{
                              fontFamily: "monospace",
                              fontWeight: 700,
                              color: "#f59e0b",
                            }}
                          >
                            #{record.id}
                          </Typography>
                        </TableCell>

                        {/* Column 2: Employee Details (employee_id, phone, first_name, last_name grouped) */}
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                          >
                            <Box>
                              <Typography sx={{ fontWeight: 600 }}>
                                {record.first_name} {record.last_name}
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                flexWrap="wrap"
                              >
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  alignItems="center"
                                >
                                  <BadgeIcon
                                    sx={{ fontSize: 12, color: "#9ca3af" }}
                                  />
                                  <Typography
                                    sx={{
                                      fontSize: "0.7rem",
                                      color: "#9ca3af",
                                    }}
                                  >
                                    ID: {record.employee_id}
                                  </Typography>
                                </Stack>
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  alignItems="center"
                                >
                                  <PhoneIcon
                                    sx={{ fontSize: 12, color: "#10b981" }}
                                  />
                                  <Typography
                                    sx={{
                                      fontSize: "0.7rem",
                                      color: "#9ca3af",
                                    }}
                                  >
                                    {record.phone || "-"}
                                  </Typography>
                                </Stack>
                              </Stack>
                            </Box>
                          </Stack>
                        </TableCell>

                        {/* Column 3: Check In */}
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <AccessTimeIcon
                                sx={{ fontSize: 15, color: "#10b981" }}
                              />
                              <Typography
                                sx={{ fontSize: "0.8rem", fontWeight: 500 }}
                              >
                                {formatDateTime(record.check_in)}
                              </Typography>
                            </Stack>
                            {record.check_in_latitude &&
                              record.check_in_longitude && (
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  alignItems="center"
                                >
                                  <LocationOnIcon
                                    sx={{ fontSize: 12, color: "#ef4444" }}
                                  />
                                  <Typography
                                    sx={{
                                      fontSize: "0.65rem",
                                      color: "#9ca3af",
                                    }}
                                  >
                                    {record.check_in_latitude},{" "}
                                    {record.check_in_longitude}
                                  </Typography>
                                </Stack>
                              )}
                            {record.is_late === 1 && (
                              <Chip
                                label={`${record.late_minutes} min late`}
                                size="small"
                                sx={{
                                  backgroundColor: "#fee2e2",
                                  color: "#991b1b",
                                  fontWeight: 600,
                                  width: "fit-content",
                                }}
                              />
                            )}
                            {record.check_in_picture && (
                              <Button
                                size="small"
                                onClick={() => {
                                  setSelectedPicture(record.check_in_picture);
                                  setCheckInModalOpen(true);
                                }}
                                sx={{
                                  textTransform: "none",
                                  color: "#3b82f6",
                                  fontSize: "0.7rem",
                                  p: 0,
                                  justifyContent: "flex-start",
                                }}
                              >
                                View Picture
                              </Button>
                            )}
                          </Stack>
                        </TableCell>

                        {/* Column 4: Check Out */}
                        <TableCell>
                          {record.check_out ? (
                            <Stack spacing={0.5}>
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <LogoutIcon
                                  sx={{ fontSize: 15, color: "#ef4444" }}
                                />
                                <Typography
                                  sx={{ fontSize: "0.8rem", fontWeight: 500 }}
                                >
                                  {formatDateTime(record.check_out)}
                                </Typography>
                              </Stack>
                              {record.check_out_latitude &&
                                record.check_out_longitude && (
                                  <Stack
                                    direction="row"
                                    spacing={0.5}
                                    alignItems="center"
                                  >
                                    <LocationOnIcon
                                      sx={{ fontSize: 12, color: "#ef4444" }}
                                    />
                                    <Typography
                                      sx={{
                                        fontSize: "0.65rem",
                                        color: "#9ca3af",
                                      }}
                                    >
                                      {record.check_out_latitude},{" "}
                                      {record.check_out_longitude}
                                    </Typography>
                                  </Stack>
                                )}
                              {record.is_early_exit === 1 && (
                                <Chip
                                  label={`${record.early_exit_minutes} min early exit`}
                                  size="small"
                                  sx={{
                                    backgroundColor: "#fef3c7",
                                    color: "#92400e",
                                    fontWeight: 600,
                                    width: "fit-content",
                                  }}
                                />
                              )}
                              {record.check_out_picture && (
                                <Button
                                  size="small"
                                  onClick={() => {
                                    setSelectedPicture(
                                      record.check_out_picture,
                                    );
                                    setCheckOutModalOpen(true);
                                  }}
                                  sx={{
                                    textTransform: "none",
                                    color: "#3b82f6",
                                    fontSize: "0.7rem",
                                    p: 0,
                                    justifyContent: "flex-start",
                                  }}
                                >
                                  View Picture
                                </Button>
                              )}
                            </Stack>
                          ) : (
                            <Chip
                              label="Not Checked Out"
                              size="small"
                              sx={{
                                backgroundColor: "#fef3c7",
                                color: "#92400e",
                                fontWeight: 600,
                              }}
                            />
                          )}
                        </TableCell>

                        {/* Column 5: Status Info (status, created_at) */}
                        <TableCell>
                          <Stack spacing={1} alignItems="center">
                            <StatusBadge status={record.status} />
                            <Typography
                              sx={{
                                fontSize: "0.7rem",
                                color: "#9ca3af",
                                textAlign: "center",
                              }}
                            >
                              {formatDateOnly(record.created_at)}
                            </Typography>
                          </Stack>
                        </TableCell>

                        {/* Column 6: Working Details (working_minutes, shift_id, break_minutes) */}
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Typography
                              sx={{ fontWeight: 700, color: "#10b981" }}
                            >
                              {formatMinutes(record.working_minutes)}
                            </Typography>
                            {record.shift_id && (
                              <Typography
                                sx={{ fontSize: "0.7rem", color: "#9ca3af" }}
                              >
                                Shift: {record.shift_id}
                              </Typography>
                            )}
                            <Typography
                              sx={{ fontSize: "0.7rem", color: "#9ca3af" }}
                            >
                              Break: {formatMinutes(record.break_minutes)}
                            </Typography>
                          </Stack>
                        </TableCell>

                        {/* Column 7: Overtime (overtime_minutes) */}
                        <TableCell>
                          <Typography
                            sx={{
                              color:
                                record.overtime_minutes > 0
                                  ? "#f59e0b"
                                  : "#9ca3af",
                              fontWeight:
                                record.overtime_minutes > 0 ? 700 : 400,
                            }}
                          >
                            {formatMinutes(record.overtime_minutes)}
                          </Typography>
                        </TableCell>

                        {/* Column 8: Approval (approval_status) */}
                        <TableCell>
                          <Chip
                            label={
                              record.approval_status?.charAt(0).toUpperCase() +
                                record.approval_status?.slice(1) || "Pending"
                            }
                            size="small"
                            sx={{
                              textTransform: "capitalize",
                              backgroundColor:
                                record.approval_status === "approved"
                                  ? "#d1fae5"
                                  : record.approval_status === "rejected"
                                  ? "#fee2e2"
                                  : "#fef3c7",
                              color:
                                record.approval_status === "approved"
                                  ? "#065f46"
                                  : record.approval_status === "rejected"
                                  ? "#991b1b"
                                  : "#92400e",
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Pagination */}
          {pagination && pagination.total_pages > 0 && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mt: 3, flexWrap: "wrap", gap: 2 }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                }}
              >
                Showing {(pagination.current_page - 1) * limit + 1} to{" "}
                {Math.min(
                  pagination.current_page * limit,
                  pagination.total_records,
                )}{" "}
                of {pagination.total_records} records
              </Typography>
              <Pagination
                count={pagination.total_pages}
                page={pagination.current_page}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                sx={{
                  "& .MuiPaginationItem-root": {
                    borderRadius: "8px",
                    "&.Mui-selected": {
                      background:
                        "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                      color: "#ffffff",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                      },
                    },
                  },
                }}
              />
            </Stack>
          )}
        </>
      )}
      {/* Picture Modal */}
      <Dialog
        open={checkInModalOpen || checkOutModalOpen}
        onClose={() => {
          setCheckInModalOpen(false);
          setCheckOutModalOpen(false);
          setSelectedPicture("");
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            backgroundColor:
              theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {checkInModalOpen ? "Check In Picture" : "Check Out Picture"}
          <IconButton
            onClick={() => {
              setCheckInModalOpen(false);
              setCheckOutModalOpen(false);
              setSelectedPicture("");
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", p: 3 }}>
          <img
            src={selectedPicture}
            alt="Attendance"
            style={{
              maxWidth: "100%",
              maxHeight: "500px",
              borderRadius: "12px",
              objectFit: "contain",
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AttendanceRecords;
